import React from 'react';

// Small animated line illustrations, one per section, drawn in the section accent (currentColor).
// All motion lives in index.css under "SECTION MOTIFS" and is disabled by prefers-reduced-motion.

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const soft = { fill: 'currentColor', opacity: 0.14 };

const Factory = () => (
  <g>
    <path {...S} className="mt-draw" d="M20 112V64l34 18V64l34 18V64l34 18V40h22v72z" />
    <rect x="142" y="22" width="12" height="20" {...S} className="mt-draw" />
    <circle className="mt-smoke s1" cx="148" cy="14" r="5" {...soft} />
    <circle className="mt-smoke s2" cx="150" cy="10" r="4" {...soft} />
    <circle className="mt-smoke s3" cx="146" cy="12" r="6" {...soft} />
    <path {...S} d="M10 124h200" />
    <path {...S} className="mt-belt" strokeDasharray="6 8" d="M10 124h200" opacity="0.6" />
    {[40, 74, 108].map((x, i) => <rect key={i} x={x} y="92" width="10" height="10" rx="2" {...S} className={`mt-blink b${i}`} />)}
  </g>
);

const Shelves = () => (
  <g>
    <path {...S} className="mt-draw" d="M40 20v104M180 20v104M40 52h140M40 88h140M40 124h140" />
    {[[56, 30], [94, 30], [132, 66], [62, 66], [100, 102], [140, 102]].map(([x, y], i) => (
      <rect key={i} x={x} y={y} width="26" height="20" rx="3" {...S} className={`mt-drop d${i}`} />
    ))}
  </g>
);

const Bitumen = () => (
  <g>
    <path {...S} className="mt-draw" d="M70 18h80v14H70zM74 32h72v52a6 6 0 0 1-6 6H80a6 6 0 0 1-6-6z" />
    <path {...S} d="M110 90v6" />
    <path className="mt-drip" fill="currentColor" d="M110 98c-4 6-6 9-6 12a6 6 0 0 0 12 0c0-3-2-6-6-12z" />
    <ellipse className="mt-ripple r1" cx="110" cy="128" rx="10" ry="3" {...S} />
    <ellipse className="mt-ripple r2" cx="110" cy="128" rx="10" ry="3" {...S} />
    <path {...S} d="M60 128h100" opacity="0.5" />
  </g>
);

const Roll = () => (
  <g>
    <g className="mt-spin" style={{ transformOrigin: '52px 84px' }}>
      <circle cx="52" cy="84" r="30" {...S} />
      <circle cx="52" cy="84" r="20" {...S} opacity="0.6" />
      <circle cx="52" cy="84" r="9" {...S} />
      <path {...S} d="M52 54v8M82 84h-8" opacity="0.6" />
    </g>
    <path className="mt-unroll" {...S} d="M52 114h150" />
    <path className="mt-unroll" {...S} strokeDasharray="3 7" d="M60 120h142" opacity="0.5" />
    <rect className="mt-unroll" x="52" y="104" width="150" height="10" {...soft} />
  </g>
);

const Ledger = () => (
  <g>
    <rect x="40" y="16" width="140" height="112" rx="8" {...S} className="mt-draw" />
    {[0, 1, 2, 3].map(i => (
      <g key={i} className={`mt-row w${i}`}>
        <path {...S} d={`M58 ${40 + i * 22}h70`} opacity="0.7" />
        <path {...S} d={i % 2 ? `M152 ${34 + i * 22}v12M146 ${40 + i * 22}l6 6 6-6` : `M152 ${46 + i * 22}v-12M146 ${40 + i * 22}l6-6 6 6`} />
      </g>
    ))}
  </g>
);

const Gears = () => {
  const gear = (cx, cy, r, teeth) => {
    const pts = [];
    for (let i = 0; i < teeth * 2; i++) {
      const a = (Math.PI * i) / teeth, rr = i % 2 ? r : r + 6;
      pts.push(`${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a)).toFixed(1)}`);
    }
    return <polygon points={pts.join(' ')} {...S} />;
  };
  return (
    <g>
      <g className="mt-spin" style={{ transformOrigin: '76px 58px' }}>{gear(76, 58, 26, 10)}<circle cx="76" cy="58" r="8" {...S} /></g>
      <g className="mt-spin-rev" style={{ transformOrigin: '128px 82px' }}>{gear(128, 82, 18, 8)}<circle cx="128" cy="82" r="6" {...S} /></g>
      <rect x="20" y="114" width="180" height="14" rx="7" {...S} />
      <path className="mt-belt" {...S} strokeDasharray="8 10" d="M28 121h164" opacity="0.6" />
    </g>
  );
};

const Truck = ({ tanker }) => (
  <g>
    <g className="mt-drive">
      {tanker
        ? <path {...S} d="M30 62h100a14 14 0 0 1 0 28H30a14 14 0 0 1 0-28z" />
        : <rect x="20" y="52" width="110" height="46" rx="4" {...S} />}
      <path {...S} d="M130 98V66h28l22 18v14z" />
      <path {...S} d="M140 70h16l12 12h-28z" opacity="0.6" />
      <g className="mt-spin" style={{ transformOrigin: '52px 106px' }}><circle cx="52" cy="106" r="10" {...S} /><path {...S} d="M52 98v16" /></g>
      <g className="mt-spin" style={{ transformOrigin: '154px 106px' }}><circle cx="154" cy="106" r="10" {...S} /><path {...S} d="M154 98v16" /></g>
    </g>
    <path className="mt-road" {...S} strokeDasharray="14 12" d="M0 126h220" opacity="0.45" />
  </g>
);

const Route = () => (
  <g>
    <path className="mt-path" {...S} strokeDasharray="6 7" d="M24 112C70 112 60 40 110 52s50 46 84-6" />
    <path {...S} d="M194 30c-8 0-14 6-14 14 0 10 14 22 14 22s14-12 14-22c0-8-6-14-14-14z" className="mt-pin" />
    <circle cx="194" cy="44" r="4" fill="currentColor" className="mt-pin" />
    <g className="mt-along"><rect x="-12" y="-8" width="24" height="16" rx="3" fill="currentColor" opacity="0.9" /></g>
  </g>
);

const Boxes2 = () => (
  <g>
    <path {...S} className="mt-draw" d="M14 120V70l30-18 30 18v50zM146 120V70l30-18 30 18v50z" />
    <path {...S} d="M34 120v-24h20v24M166 120v-24h20v24" opacity="0.6" />
    <path {...S} strokeDasharray="5 7" d="M54 64C80 18 140 18 166 64" opacity="0.5" />
    <rect className="mt-hop" x="100" y="20" width="20" height="16" rx="3" fill="currentColor" />
  </g>
);

const Coins = () => (
  <g>
    {[0, 1, 2, 3].map(i => (
      <g key={i} className={`mt-coin c${i}`}>
        <ellipse cx="80" cy={118 - i * 14} rx="34" ry="9" {...S} />
        <path {...S} d={`M46 ${118 - i * 14}v-8M114 ${118 - i * 14}v-8`} opacity="0.6" />
      </g>
    ))}
    <clipPath id="mt-tick-win"><rect x="122" y="52" width="76" height="40" rx="8" /></clipPath>
    <rect x="122" y="52" width="76" height="40" rx="8" {...S} opacity="0.5" />
    <g clipPath="url(#mt-tick-win)">
      <g className="mt-ticker">
        {['$', '﷼', 'SM', 'сўм', '$'].map((c, i) => (
          <text key={i} x="160" y={80 + i * 40} textAnchor="middle" fill="currentColor" fontSize="22" fontWeight="700">{c}</text>
        ))}
      </g>
    </g>
  </g>
);

const Cart = () => (
  <g>
    <g className="mt-swing" style={{ transformOrigin: '150px 22px' }}>
      <path {...S} d="M150 22v12" />
      <path {...S} d="M134 34h32l8 10-24 34-24-34z" />
      <circle cx="150" cy="48" r="4" {...S} />
    </g>
    <path {...S} className="mt-draw" d="M20 40h18l14 56h64l12-40H46" />
    <circle cx="62" cy="112" r="8" {...S} /><circle cx="108" cy="112" r="8" {...S} />
    {[0, 1, 2].map(i => <circle key={i} className={`mt-bounce k${i}`} cx={66 + i * 18} cy="70" r="7" fill="currentColor" opacity="0.85" />)}
  </g>
);

const Bars = () => (
  <g>
    <path {...S} d="M24 124h176M24 124V20" opacity="0.6" />
    {[[40, 60], [72, 40], [104, 78], [136, 56], [168, 92]].map(([x, h], i) => (
      <rect key={i} className={`mt-grow g${i}`} x={x} y={124 - h} width="20" height={h} rx="3" {...S} style={{ transformOrigin: `${x + 10}px 124px` }} />
    ))}
    <path className="mt-trend" {...S} d="M50 70L82 52 114 38 146 50 178 22" />
  </g>
);

const People = () => (
  <g>
    <ellipse className="mt-orbit" cx="110" cy="76" rx="92" ry="34" {...S} strokeDasharray="4 8" opacity="0.5" />
    {[[70, 0.7], [110, 1], [150, 0.7]].map(([x, s], i) => (
      <g key={i} className={`mt-breathe p${i}`}>
        <circle cx={x} cy={62} r={12 * s} {...S} />
        <path {...S} d={`M${x - 20 * s} ${100}c0-14 ${9 * s}-22 ${20 * s}-22s${20 * s} 8 ${20 * s} 22`} />
      </g>
    ))}
  </g>
);

const Audit = () => (
  <g>
    <rect x="50" y="16" width="100" height="116" rx="8" {...S} className="mt-draw" />
    <rect x="80" y="10" width="40" height="14" rx="4" {...S} />
    {[0, 1, 2, 3].map(i => <path key={i} {...S} className={`mt-row w${i}`} d={`M66 ${46 + i * 20}h68`} opacity="0.7" />)}
    <g className="mt-scan">
      <circle cx="160" cy="64" r="16" {...S} />
      <path {...S} d="M172 76l16 16" />
    </g>
  </g>
);

const MOTIFS = {
  dashboard: Factory, warehouses: Shelves, raw_materials: Bitumen, finished_goods: Roll, inventory: Ledger,
  production: Gears, purchases: () => <Truck tanker />, sales: Cart, lading_bills: Route, transfers: Boxes2,
  accounting: Coins, reports: Bars, users: People, audit_log: Audit,
};

export default function Motif({ section }) {
  const M = MOTIFS[section] || Factory;
  return (
    <svg viewBox="0 0 220 140" className="hero-motif" aria-hidden="true" focusable="false"><M /></svg>
  );
}
